import axios from "../../axios-auth";
import { isValidJwtToken } from "../../helpers/helpers";
import constants from "../../helpers/constants";
import { i18n } from "../../i18n.js";
import hash from "object-hash";
import { isEmpty, pathOr, pluck } from "ramda";

const state = {
  token: null,
  prevValidateSelection: {},
  resetSelection: null,
  activateResetSelection: false,
  prevPricesChangeOptionsData: {},
  pricesMatrixResponse: {},
  pricesMatrixActiveQty: null,
  pricesMatrixActiveProdSpeed: null,
  hiddenPageOptions: [],
  pricesFailed: false
};

const getters = {
  getCimpressPricesFailed: state => {
    return state.pricesFailed;
  },
  getCimpresPricesMatrixActiveQty: state => {
    return state.pricesMatrixActiveQty;
  },
  getCimpresPricesMatrixActiveProdSpeed: state => {
    return state.pricesMatrixActiveProdSpeed;
  },
  getCimpresPricesMatrixQtyHeaders: state => {
    return Object.keys(state.pricesMatrixResponse);
  },
  getCimpresPricesMatrixQtyData: state => {
    const data = state.pricesMatrixResponse;
    const qtyData = {};
    for (const production in data) {
      for (const qty in data[production]) {
        qtyData[qty] = {
          ...qtyData[qty],
          quantity: data[production][qty].quantity,
          [production]: data[production][qty]
        };
      }
    }
    return qtyData;
  },
  getCimpresPricesMatrixResponse: state => {
    return state.pricesMatrixResponse;
  },
  getPrevPricesChangeOptionsData: state => {
    return state.prevPricesChangeOptionsData;
  },
  getCimpressNoQuantity: state => {
    return true;
  },
  getCimpressNoQuantityAllValuesSelected: (state, getters) => {
    const ppName = getters.getCimpressProductId;
    const attributeResolveStrategy =
      getters.getCimpressAttributeResolveStrategy[ppName];
    let attributes = Object.keys(attributeResolveStrategy).filter(item => {
      if (attributeResolveStrategy[item] == null) {
        return true;
      }
      return false;
    });

    if (attributes.includes("Quantity")) {
      attributes = attributes.filter(item => item != "Quantity");
    }
    if (attributes.includes("ProductionSpeed")) {
      attributes = attributes.filter(item => item != "ProductionSpeed");
    }

    return attributes.length == 0;
  },
  getCimpressToken: state => {
    return state.token;
  },
  getCimpressValidateSelection: (state, getters) => {
    const postData = getters.getChangeOptionsPostData(true);
    const ppName = postData.cimpress_product_id;
    const attributes = Object.keys(postData).filter(item => {
      if (
        item.startsWith("print_options[PP_" + ppName + "]") &&
        postData[item]
      ) {
        return true;
      }
      return false;
    });
    const data = {};
    for (var attr in attributes) {
      var key = attributes[attr];
      var new_key = key
        .replace("print_options[PP_" + ppName + "][", "")
        .replace("][]", "");
      data[new_key] = postData[key];
    }
    if (
      getters.getCimpressNoQuantity &&
      !getters.getCimpressNoQuantityAllValuesSelected
    ) {
      if (data.hasOwnProperty("Quantity")) {
        delete data.Quantity;
      }
      if (data.hasOwnProperty("ProductionSpeed")) {
        delete data.ProductionSpeed;
      }
    }

    if (getters.getCimpressNoQuantity) {
      if (getters.getCimpresPricesMatrixActiveQty) {
        data.Quantity = getters.getCimpresPricesMatrixActiveQty;
      }
      if (getters.getCimpresPricesMatrixActiveProdSpeed) {
        data.ProductionSpeed = getters.getCimpresPricesMatrixActiveProdSpeed;
      }
    }
    return data;
  },
  getCimpressPrevValidateSelection: state => {
    return state.prevValidateSelection;
  },
  getCimpressValidateUrl: (state, getters) => {
    const config = getters.getConfig;
    return getters.getIsCimpressProduct && config.cimpress_validate_url
      ? config.cimpress_validate_url
      : null;
  },
  getCimpressTokenUrl: (state, getters) => {
    const config = getters.getConfig;
    return getters.getIsCimpressProduct && config.cimpress_get_token_url
      ? config.cimpress_get_token_url
      : null;
  },
  getIsCimpressDefaultSelection: (state, getters) => {
    const config = getters.getInstanceConfig;
    if (config) {
      return config.isCimpressDefaultSelection;
    }
    return false;
  },
  getIsCimpressPreferredSelection: (state, getters) => {
    const config = getters.getInstanceConfig;
    if (config) {
      return config.isCimpressPreferredSelection;
    }
    return false;
  },
  getActivateResetSelection: state => {
    return state.activateResetSelection;
  },
  getResetSelection: state => {
    return state.resetSelection;
  },
  getCimpressHiddenPageOptions: state => {
    return state.hiddenPageOptions;
  }
};

const mutations = {
  setCimpressHiddenPageOptions: (state, payload) => {
    state.hiddenPageOptions = payload;
  },
  setCimpressPricesFailed: (state, payload) => {
    state.pricesFailed = payload;
  },
  setCimpressToken: (state, payload) => {
    state.token = payload;
  },
  setCimpressPrevValidateSelection: (state, payload) => {
    state.prevValidateSelection = payload;
  },
  setActivateResetSelection: (state, payload) => {
    state.activateResetSelection = payload;
  },
  setResetSelection: (state, payload) => {
    state.resetSelection = payload;
  },
  setPrevPricesChangeOptionsData: (state, payload) => {
    state.prevPricesChangeOptionsData = payload;
  },
  setCimpresPricesMatrixResponse: (state, payload) => {
    state.pricesMatrixResponse = payload;
  },
  setCimpresPricesMatrixActiveQty: (state, payload) => {
    state.pricesMatrixActiveQty = payload;
  },
  setCimpresPricesMatrixActiveProdSpeed: (state, payload) => {
    state.pricesMatrixActiveProdSpeed = payload;
  }
};

const actions = {
  setCimpressHiddenPageOptions: ({ commit }, payload) => {
    commit("setCimpressHiddenPageOptions", payload);
  },
  setCimpressPricesFailed: ({ commit }, payload) => {
    commit("setCimpressPricesFailed", payload);
  },
  setActivateResetSelection: ({ commit }, payload) => {
    commit("setActivateResetSelection", payload);
  },
  setCimpressPrevValidateSelection: ({ commit }, payload) => {
    commit("setCimpressPrevValidateSelection", payload);
  },
  setPrevPricesChangeOptionsData: ({ commit }, payload) => {
    commit("setPrevPricesChangeOptionsData", payload);
  },
  setCimpresPricesMatrixResponse: ({ commit, getters }, payload) => {
    if (payload.length == 0) {
      commit("setCimpressPricesFailed", true);
      getters.getSweetAlert({
        text: i18n.t(
          "Dear Customer, unfortunately we were unable to get a valid price for your configuration."
        ),
        icon: "warning"
      });
    } else {
      commit("setCimpressPricesFailed", false);
    }
    commit("setCimpresPricesMatrixResponse", payload);
  },
  setCimpresPricesMatrixActiveQty: ({ commit, dispatch }, payload) => {
    commit("setCimpresPricesMatrixActiveQty", payload);
    if (payload) {
      dispatch("setQuantityNoChangeOptions", { qty: payload });
    }
  },
  setCimpresPricesMatrixActiveQtyCustom: ({ commit, dispatch }, payload) => {
    commit("setCimpresPricesMatrixActiveQty", payload);
  },
  setCimpresPricesMatrixActiveProdSpeed: ({ commit, dispatch }, payload) => {
    commit("setCimpresPricesMatrixActiveProdSpeed", payload);
    if (payload) {
      dispatch("setPageOptionCimpressOneValue", {
        option: "ProductionSpeed",
        value: payload
      });
    }
  },
  validateCimpress: ({ commit, dispatch, getters }, payload) => {
    commit("setIsChangeOptionsLoading", true);

    if (getters.getCimpressToken && isValidJwtToken(getters.getCimpressToken)) {
      // validate agains cimpress
      dispatch("validateCimpressWithToken", payload);
    } else {
      // getToken
      axios.instance
        .get(getters.getCimpressTokenUrl)
        .then(response => {
          if (!response.data.success) {
            console.log("Get Token Error : " + response.data.message);
            throw new Error("Could not get token");
          }
          var responseData = response.data;
          commit("setCimpressToken", responseData.data.token);
          dispatch("validateCimpressWithToken", payload);
        })
        .catch(error => {
          console.log(error);
          commit("setIsChangeOptionsLoading", false);
        });
    }
  },
  validateCimpressWithToken: ({ commit, dispatch, getters }, payload) => {
    const cimpressValidateSelection = getters.getCimpressValidateSelection;
    if (
      hash(cimpressValidateSelection) ==
      getters.getCimpressPrevValidateSelection
    ) {
      cimpressMoveToNextItem(dispatch, getters, payload);
      return;
    }

    const requestData = {
      includeInformationalAttribute: true,
      applyImpliedInState: true,
      includeBuckets: ["option"],
      selections: cimpressValidateSelection
    };
    const url = getters.getCimpressValidateUrl;

    const headers = {
      Authorization: "Bearer " + getters.getCimpressToken,
      "Content-Type": "application/json"
    };

    // qty ????

    axios.instance
      .post(url, requestData, { headers })
      .then(response => {
        if (response.data && response.data.state) {
          if (constants.CIMPRESS_STATE_FULLY_RESOLVED === response.data.state) {
            commit(
              "setCimpressPrevValidateSelection",
              hash(cimpressValidateSelection)
            );
            if (!getters.getIsCimpressDefaultSelection) {
              if (getters.getCimpressNoQuantity) {
                const cimpressData = getters.getCimpressChangeOptionsData;
                const cimpressDataAttributes = computeCimpressDataAttributes(
                  response.data,
                  getters
                );
                dispatch("setCimpressChangeOptionsData", {
                  ...cimpressData,
                  ...cimpressDataAttributes
                });

                dispatch(
                  "setCimpressChangeOptionsDataStatus",
                  constants.CIMPRESS_STATE_FULLY_RESOLVED
                );

                dispatch("setIsChangeOptionsLoading", false);
              } else {
                dispatch("runChangeOptions", {
                  ...payload,
                  changeOptionsLoadingStarted: true
                });
              }
            } else {
              const cimpressData = getters.getCimpressChangeOptionsData;
              const cimpressDataAttributes = computeCimpressDataAttributes(
                response.data,
                getters
              );
              dispatch("setCimpressChangeOptionsData", {
                ...cimpressData,
                ...cimpressDataAttributes
              });
              cimpressMoveToNextItem(dispatch, getters, payload);
              dispatch("setIsChangeOptionsLoading", false);
            }
          } else if (
            response.data.state == constants.CIMPRESS_STATE_PARTIALLY_RESOLVED
          ) {
            commit(
              "setCimpressPrevValidateSelection",
              hash(cimpressValidateSelection)
            );
            const cimpressData = getters.getCimpressChangeOptionsData;
            const cimpressDataAttributes = computeCimpressDataAttributes(
              response.data,
              getters
            );
            dispatch("setCimpressChangeOptionsData", {
              ...cimpressData,
              ...cimpressDataAttributes
            });
            cimpressMoveToNextItem(dispatch, getters, payload);

            if (!getters.getIsCimpressDefaultSelection) {
              if (
                getters.getCimpressNoQuantity &&
                getters.getCimpressNoQuantityAllValuesSelected &&
                !getters.getCimpresPricesMatrixActiveQty &&
                !getters.getCimpresPricesMatrixActiveProdSpeed
              ) {
                dispatch("setIsChangeOptionsLoading", true);
                dispatch("runChangeOptions", {
                  ...payload,
                  changeOptionsLoadingStarted: true,
                  changeOptionsGetPricesForProduction: true
                });
                return;
              }
            }
          } else if (
            response.data.state == constants.CIMPRESS_STATE_UNRESOLVABLE
          ) {
            getters.getSweetAlert({
              text: i18n.t("Invalid selection"),
              icon: "warning"
            });
            dispatch(
              "setChangeOptionsData",
              JSON.parse(JSON.stringify(getters.getOldChangeOptionsData))
            );
            dispatch("setIsChangeOptionsLoading", false);
          }
        }
      })
      .catch(error => {
        console.log(error);
        dispatch("setIsChangeOptionsLoading", false);
      });
  },
  resetSelection: ({ commit, getters, dispatch }, payload) => {
    const data_ppOpt = getters.getChangeOptionsDataPPOpt;
    let updatedValues = {};
    for (let option in data_ppOpt) {
      if (option.startsWith("PP_")) {
        for (let attribute in data_ppOpt[option]) {
          updatedValues["print_options[" + option + "][" + attribute + "][]"] =
            "";
        }
      }
    }

    commit("setResetSelection", updatedValues);
    commit("setActivateResetSelection", true);
    dispatch("runChangeOptions", {});
  }
};

const computeCimpressDataAttributes = (result, getters) => {
  const informationalAttributes = {};
  const remainingOptions = {};
  const ppOptions =
    getters.getChangeOptionsDataPPOpt["PP_" + getters.getCimpressProductId];
  const oldChangeOptionsData =
    getters.getOldChangeOptionsData.pp_opt[
      "PP_" + getters.getCimpressProductId
    ];

  const informationalAttributesKeys = [];
  const resolvedAttributesStrategy = {};
  for (const key in result.attributes) {
    const attribute = result.attributes[key];
    const attributeKey = attribute.key;
    const isInformationalAttribute = attribute.isInformationalAttribute
      ? attribute.isInformationalAttribute
      : false;
    const resolvedValue = attribute.resolvedValue
      ? attribute.resolvedValue
      : null;
    if (resolvedValue) {
      if (!isInformationalAttribute) {
        resolvedAttributesStrategy[attributeKey] = attribute.resolutionStrategy;
        ppOptions[attributeKey] = [resolvedValue];
        oldChangeOptionsData[attributeKey] = [resolvedValue];
      } else {
        informationalAttributes[attributeKey] = resolvedValue;
      }
    } else {
      resolvedAttributesStrategy[attributeKey] = null;
    }
    if (isInformationalAttribute) {
      informationalAttributesKeys.push(attributeKey);
    }
  }

  for (const key in result.remainingOptions) {
    const option = result.remainingOptions[key];

    if (informationalAttributesKeys.includes(option.key)) {
      continue;
    }
    const isRange = pathOr(null, ["values", "0", "type"], option) === "range";
    if (isRange) {
      const rangeValues = option.values.map(el => {
        if (el.type == "numberLiteral") {
          return {
            minimum: parseInt(el.numberLiteral),
            maximum: parseInt(el.numberLiteral)
          };
        }
        return el.range;
      });
      remainingOptions[option.key] = {
        isRange: true,
        ranges: rangeValues
      };
    } else {
      const type = pathOr(null, ["type"], option);
      let pluckKey = "";
      switch (type) {
        case "string":
        case "unspecified":
          pluckKey = "stringLiteral";
          break;
        case "numeric":
          pluckKey = "numberLiteral";
          break;
      }
      if (!isEmpty(option.values)) {
        remainingOptions[option.key] = {
          isRange: false,
          values: pluck(pluckKey, option.values)
        };
      }
    }
  }

  return {
    informationalAttributes,
    remainingOptions: {
      [getters.getCimpressProductId]: remainingOptions
    },
    resolvedAttributesStrategy: {
      [getters.getCimpressProductId]: resolvedAttributesStrategy
    }
  };
};

const cimpressMoveToNextItem = (dispatch, getters, payload) => {
  const oldChangeOptionsData = JSON.parse(
    JSON.stringify(getters.getOldChangeOptionsData)
  );
  let pageOption = getters.getActivePageOptions;
  if (payload.layout == false) {
    pageOption = payload.option;
  }
  oldChangeOptionsData.pp_opt["PP_" + getters.getActiveProductPartCode][
    pageOption
  ] = [payload.value];
  oldChangeOptionsData.cimpress = { ...getters.getCimpressChangeOptionsData };

  dispatch("setOldChangeOptionsData", oldChangeOptionsData);
  dispatch("moveToNextPageOption", payload);

  dispatch("setIsChangeOptionsLoading", false);
};

export default {
  state,
  mutations,
  actions,
  getters
};
